import { supabaseClient } from "@/lib/supabase";
import type { PatientT } from "@patient-watch/types/database";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { createProfileQueryKey, invalidateProfileQuery, patchProfileQueryData } from "./selectProfile";
import type { ProfileT } from "@patient-watch/types/database";

type PartialPatient = Partial<PatientT>;

export async function updatePatient(patientId: string, data: PartialPatient) {
    return await supabaseClient
        .from("patients")
        .update(data)
        .eq("patient_id", patientId)
        .select()
        .throwOnError()
        .single()
        .then((result) => result.data);
}

function usePatientMutation(userId: string) {
    const queryClient = useQueryClient();
    const toastCopy = useToastCopy();
    const profileQueryKey = createProfileQueryKey(userId);

    return useMutation({
        mutationFn: (data: PartialPatient) => updatePatient(userId, data),

        onMutate: async (data: PartialPatient) => {
            await queryClient.cancelQueries({ queryKey: profileQueryKey });
            const previousProfile = queryClient.getQueryData<ProfileT | null>(profileQueryKey);
            patchProfileQueryData(queryClient, userId, data);
            return { previousProfile };
        },
        onSuccess: () => {
            toast.success(toastCopy.profileUpdatedSuccess);
        },

        onError: (error, _variables, context) => {
            if (context?.previousProfile !== undefined) {
                queryClient.setQueryData(profileQueryKey, context.previousProfile);
            }
            toast.error(error.message);
        },
        onSettled: () => {
            void invalidateProfileQuery(queryClient, userId);
        },
    });
}

export default usePatientMutation;
